class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    // #CartDrawer-Upsell is recreated on every renderContents(), so the click
    // listener lives on the outer <cart-drawer> element (never replaced) and
    // delegates down, instead of being rebound after each render.
    this.addEventListener('click', this.onUpsellAddClick.bind(this));

    // Quantity/remove changes on an already-open drawer go through cart.js's
    // CartItems.updateQuantity(), which swaps .drawer__inner itself and only
    // publishes cartUpdate afterwards — renderContents() is never called for
    // that path, so the upsell strip and the shipping bar need their own hook
    // here. (Add-to-cart flows call renderContents() directly, which already
    // refreshes both right after its own DOM swap — reacting to their event
    // here too would just race a node renderContents is about to replace.)
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'cart-items') return;
      this.renderUpsell();
      this.animateFreeShippingBar();
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    // First time opening after a page load has no renderContents() call to
    // trigger this from, so refresh the upsell block and animate the
    // shipping bar here too.
    this.renderUpsell();
    this.animateFreeShippingBar();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    let renderFailed = false;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const sectionHTML = parsedState.sections && parsedState.sections[section.id];
      if (!sectionHTML) {
        renderFailed = true;
        return;
      }
      try {
        sectionElement.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
      } catch (error) {
        renderFailed = true;
        console.error('[CartDrawer] falha ao renderizar a seção', section.id, error);
      }
    });

    // /cart/add.js responses carry product_id at the top level for a single-line
    // add, or under items[] for a multi-line add — either way this is the item
    // that was just added, which is a more reliable upsell seed than guessing
    // "most recent" from cart.items order (Shopify doesn't guarantee that order).
    const seedProductId =
      parsedState.product_id ||
      (Array.isArray(parsedState.items) && parsedState.items.length
        ? parsedState.items[parsedState.items.length - 1].product_id
        : undefined);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.renderUpsell(seedProductId);
      this.animateFreeShippingBar();
    });

    // Uma seção pode vir incompleta em conexões instáveis (ex: mobile); em vez de
    // deixar o drawer aberto com conteúdo em branco, busca o estado atual no servidor.
    if (renderFailed) this.refreshFromServer();
  }

  // Animates the shipping-progress fill from 0 up to its real width every time
  // the drawer (re)renders. The bar is always painted server-side with its
  // final width already set, so without this a CSS transition has nothing to
  // transition from — it never actually plays.
  animateFreeShippingBar() {
    const fill = this.querySelector('.cart-drawer__free-shipping-bar-fill');
    if (!fill) return;

    const targetWidth = fill.style.width;
    fill.style.transition = 'none';
    fill.style.width = '0%';
    fill.getBoundingClientRect();
    requestAnimationFrame(() => {
      fill.style.transition = '';
      fill.style.width = targetWidth;
    });
  }

  // Renders a "você também pode gostar" strip in #CartDrawer-Upsell based on
  // Shopify's Product Recommendations API, seeded from the most recent cart
  // line. Complementary recommendations need order history to work, so this
  // falls back to related products until the store accumulates enough of it.
  renderUpsell(seedProductId) {
    const container = this.querySelector('#CartDrawer-Upsell');
    if (!container) return;

    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        if (!cart.items.length) {
          container.hidden = true;
          container.innerHTML = '';
          return;
        }

        const baseProductId = seedProductId || cart.items[0].product_id;
        const cartProductIds = new Set(cart.items.map((item) => item.product_id));

        return this.fetchRecommendations(baseProductId, 'complementary')
          .then((products) => (products.length ? products : this.fetchRecommendations(baseProductId, 'related')))
          .then((products) => {
            const filtered = products.filter((product) => !cartProductIds.has(product.id) && product.available).slice(0, 4);

            if (!filtered.length) {
              container.hidden = true;
              container.innerHTML = '';
              return;
            }

            container.hidden = false;
            container.innerHTML = this.getUpsellMarkup(filtered);
          });
      })
      .catch(() => {
        container.hidden = true;
        container.innerHTML = '';
      });
  }

  fetchRecommendations(productId, intent) {
    return fetch(`${routes.product_recommendations_url}.json?product_id=${productId}&limit=6&intent=${intent}`)
      .then((response) => (response.ok ? response.json() : { products: [] }))
      .then((data) => data.products || [])
      .catch(() => []);
  }

  getUpsellMarkup(products) {
    const cards = products
      .map((product) => {
        const variant = product.variants[0];
        return `
          <div class="cart-drawer__upsell-card">
            <a href="${product.url}" class="cart-drawer__upsell-image" tabindex="-1" aria-hidden="true">
              <img src="${product.featured_image}" alt="" loading="lazy" width="120" height="120">
            </a>
            <a href="${product.url}" class="cart-drawer__upsell-name">${this.escapeHtml(product.title)}</a>
            <span class="cart-drawer__upsell-price">${this.formatMoney(variant.price)}</span>
            <button
              type="button"
              class="button button--secondary cart-drawer__upsell-add"
              data-upsell-add-button
              data-variant-id="${variant.id}"
            >
              Adicionar
            </button>
          </div>
        `;
      })
      .join('');

    return `
      <p class="cart-drawer__upsell-title">Você também pode gostar</p>
      <div class="cart-drawer__upsell-list">${cards}</div>
    `;
  }

  onUpsellAddClick(event) {
    const button = event.target.closest('[data-upsell-add-button]');
    if (!button) return;
    if (button.hasAttribute('aria-disabled')) return;

    button.setAttribute('aria-disabled', true);
    button.classList.add('loading');
    const originalText = button.textContent;
    button.textContent = '...';

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', button.dataset.variantId);
    formData.append('quantity', 1);
    formData.append('sections', this.getSectionsToRender().map((section) => section.id));
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.error('[CartDrawer] falha ao adicionar sugestão', response.description || response.message);
          return;
        }
        this.renderContents(response);
      })
      .catch((error) => console.error('[CartDrawer] falha ao adicionar sugestão', error))
      .finally(() => {
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        button.textContent = originalText;
      });
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  refreshFromServer() {
    if (this.isRefreshingFromServer) return;
    this.isRefreshingFromServer = true;
    const sectionIds = this.getSectionsToRender().map((section) => section.id);
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => (response.ok ? response.text() : Promise.reject()))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshDrawer = doc.getElementById('CartDrawer');
        const currentDrawer = document.getElementById('CartDrawer');
        if (freshDrawer && currentDrawer) currentDrawer.innerHTML = freshDrawer.innerHTML;
      })
      .catch(() => console.error('[CartDrawer] não foi possível recarregar o carrinho automaticamente'))
      .finally(() => {
        this.isRefreshingFromServer = false;
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
